import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CookieBanner } from '../components/CookieBanner';
import { useLanguage } from '../contexts/LanguageContext';
const privacyContent = {
  en: {
    title: 'Privacy',
    titleHighlight: 'Policy',
    lastUpdated: 'Last updated: February 3, 2026',
    sections: [{
      title: '1) Who we are',
      content: `IT Monsters is an IT agency creating modern websites for clients worldwide.

**Data Controller:** Sole Proprietor (FOP) Maksym Pavlovych Tovkach, registered in Ukraine, Tax ID 3606206351.

**Privacy contact:**
- Email: maximtovkach98@gmail.com
- Phone: +380 50 839 93 09

You can also contact us via Telegram/WhatsApp using the contact options on our website.`
    }, {
      title: '2) Scope',
      content: `This Policy applies to:
- visitors of our website;
- people who submit inquiries via our form;
- people who contact us via messenger apps, phone, or email;
- clients who provide materials needed to build a website.`
    }, {
      title: '3) What data we collect',
      content: `**3.1 Data you provide via the website form**
We may collect: Name, Email, Phone number. Optional: Message/comment.
Form submissions may be delivered to us (for example, to a messenger channel) so we can follow up with you.

**3.2 Data you provide for website creation**
If you become a client, you may provide materials and information needed to create your website, such as: project/company name; texts (services, descriptions); images, logo, brand materials; any other files or information you voluntarily send us.
We upload and use these materials only to perform the requested services.

**3.3 Communications**
If you contact us via Telegram/WhatsApp or email, the conversation is stored in the relevant service (messenger/email). We do not delete messages unless requested and unless we are legally required to keep certain records.

**3.4 Technical data and cookies**
When you visit our website, technical data (e.g., IP address, device/browser data, cookies, logs) may be processed to operate the site and ensure reliable delivery.
Our website may be built and/or delivered using third-party services (including AI-assisted website creation tools). Those services may process certain technical data under their own privacy policies.`
    }, {
      title: '4) Why we use your data',
      content: `We process personal data to:
- contact you and respond to your inquiry;
- understand your request and discuss your future website;
- provide a free demo/preview if agreed;
- deliver the service (website creation) and handle revisions;
- keep records required by applicable law (including accounting/tax records under Ukrainian law).`
    }, {
      title: '5) Legal bases',
      content: `Depending on your location, we rely on:
- **Consent** (e.g., checkbox on the form, cookie choices);
- **Pre-contract steps / Contract performance** (to provide the service you request);
- **Legal obligation** (e.g., mandatory accounting/tax record keeping);
- **Legitimate interests** (limited to what is necessary to operate and protect the service).`
    }, {
      title: '6) Cookies and advertising (Google Ads)',
      content: `We use a cookie banner that allows you to accept or decline certain cookies.
We may use Google Ads to promote our services. Depending on your region and settings, this may involve advertising or conversion-measurement cookies. If you decline relevant cookies, we will limit such technologies to the extent possible.`
    }, {
      title: '7) Sharing data with third parties',
      content: `We do not sell personal data.
We may share data only as necessary to provide our services, including:
- communication services (Telegram/WhatsApp) to communicate with you and receive inquiries;
- infrastructure services (e.g., Cloudflare) to deliver site traffic and help route form submissions;
- website creation services (including AI tools, such as Magic Patterns) used to build client projects;
- contractors/employees assigned to your project (only those who actually work on it, and only with necessary access);
- legal disclosures when required by law.`
    }, {
      title: '8) International transfers',
      content: `Because we serve clients worldwide, data may be processed in different countries depending on the services used (e.g., messengers, advertising, infrastructure). Where required, we apply appropriate safeguards.`
    }, {
      title: '9) Payments',
      content: `We do not accept payments directly on the website. Payment details (bank transfer / crypto, etc.) may be provided during personal communication. We do not process or store bank card details on the website.`
    }, {
      title: '10) Data retention',
      content: `We keep communications and client materials as long as necessary for the project and record-keeping. Certain records (e.g., accounting documents) may be retained as required by Ukrainian law.
We delete data manually upon request, unless we must keep it for legal reasons.`
    }, {
      title: '11) Your rights',
      content: `You may contact us to request access, correction, or deletion of your personal data, or to ask us to stop contacting you.
You can withdraw your consent at any time by contacting us using the details above.
For users in the EU/EEA and UK, additional GDPR/UK GDPR rights may apply (access, rectification, erasure, restriction, portability, objection, withdrawal of consent). If you are located in the EU/EEA or the UK, you also have the right to lodge a complaint with your local data protection authority.`
    }, {
      title: '12) Security',
      content: `We use reasonable organizational measures:
- data is controlled by the owner and shared only with the manager/contractor assigned to your project;
- we limit access to what is necessary for delivery.`
    }, {
      title: '13) Children',
      content: `Our services are not intended for children, and we do not knowingly collect children's data.`
    }, {
      title: '14) Call recordings',
      content: `We may occasionally record calls to document agreements. You can ask us to stop recording or delete a recording where feasible and lawful.`
    }, {
      title: '15) Changes',
      content: `We may update this Policy from time to time. The current version is the one published on our website.`
    }]
  },
  ru: {
    title: 'Политика',
    titleHighlight: 'конфиденциальности',
    lastUpdated: 'Последнее обновление: 3 февраля 2026',
    sections: [{
      title: '1) Кто мы',
      content: `IT Monsters — IT-студия, которая делает современные сайты для клиентов по всему миру.

**Контролёр данных:** ФОП Товкач Максим Павлович (Украина), ИНН 3606206351.

**Контакты по вопросам приватности:**
- Email: maximtovkach98@gmail.com
- Телефон: +380 50 839 93 09

Также можно написать в Telegram/WhatsApp через контакты на нашем сайте.`
    }, {
      title: '2) На кого распространяется',
      content: `Политика применяется к:
- посетителям сайта;
- тем, кто оставляет заявку;
- тем, кто пишет нам в мессенджеры/на почту или звонит;
- клиентам, которые передают материалы для создания сайта.`
    }, {
      title: '3) Какие данные мы собираем',
      content: `**3.1 Данные из формы на сайте**
Мы можем получать: имя, email, номер телефона. По желанию: сообщение/комментарий.
Заявка с сайта может поступать нам (например, в канал коммуникации в мессенджере) для связи с вами.

**3.2 Данные для создания сайта**
Для выполнения услуги клиент может передать: название проекта/компании; тексты (услуги, описания); изображения, логотип, бренд-материалы; любые другие файлы/данные, которые клиент отправляет добровольно.
Мы используем эти материалы только для выполнения работ.

**3.3 Переписка**
Если вы пишете нам в Telegram/WhatsApp или на email, переписка хранится в соответствующем сервисе. Мы не удаляем сообщения, если нет запроса на удаление и если закон не требует хранения некоторых данных.

**3.4 Технические данные и cookies**
При посещении сайта могут обрабатываться технические данные (например, IP-адрес, данные браузера/устройства, cookies, логи) для работы сайта и надёжной доставки контента.
Сайт может использовать сторонние сервисы (в том числе инструменты создания сайтов с использованием ИИ). Такие сервисы могут обрабатывать технические данные согласно своим политикам.`
    }, {
      title: '4) Зачем мы используем данные',
      content: `Данные нужны, чтобы:
- связаться с вами и ответить на заявку;
- обсудить будущий сайт и предложить варианты;
- подготовить бесплатное демо/превью (если договорились);
- выполнить заказ и внести правки;
- хранить документы, если этого требует законодательство Украины (включая бухгалтерию).`
    }, {
      title: '5) Основания обработки',
      content: `В зависимости от страны пользователя мы используем:
- **Согласие** (галочка в форме, выбор cookies);
- **Исполнение договорённости/договора** (когда вы заказываете услугу);
- **Юридическую обязанность** (учёт/налоги);
- **Законный интерес** (в пределах, необходимых для работы и защиты сервиса).`
    }, {
      title: '6) Cookies и реклама (Google Ads)',
      content: `На сайте есть баннер cookies с выбором принять или отклонить.
Мы можем использовать Google Ads для реклами. Это может включать рекламные и/или конверсионные cookies в зависимости от настроек и требований закона. Если вы отклоняете соответствующие cookies — мы ограничиваем такие технологии в рамках доступных настроек.`
    }, {
      title: '7) Кому мы передаём данные',
      content: `Мы не продаём персональные данные.
Передача возможна только при необходимости:
- Telegram/WhatsApp (для коммуникации и получения заявок);
- инфраструктурные сервисы (например, Cloudflare) для доставки трафика и маршрутизации отправки заявок;
- сервисы создания сайтов (в т.ч. с использованием ИИ, например Magic Patterns) для выполнения клиентских проектов;
- менеджер/исполнитель, назначенный на ваш проект (только нужный доступ);
- по требованию закона.`
    }, {
      title: '8) Международная обработка',
      content: `Так как мы работаем по всему миру, данные могут обрабатываться в разных странах в зависимости от используемых сервисов (мессенджеры, реклама, инфраструктура). При необходимости применяются соответствующие меры защиты.`
    }, {
      title: '9) Оплата',
      content: `Мы не принимаем оплату напрямую на сайте. Реквизиты для оплаты (банковский счёт / крипто и т.д.) могут предоставляться в личном общении. Данные банковских карт на сайте мы не обрабатываем и не храним.`
    }, {
      title: '10) Сроки хранения',
      content: `Переписка и материалы клиента хранятся столько, сколько нужно для проекта и ведения записей. Отдельные документы (например, бухгалтерские/договорные) могут храниться согласно законодательству Украины.
Удаление — вручную по запросу, если нет обязанности хранить данные по закону.`
    }, {
      title: '11) Ваши права',
      content: `Вы можете обратиться к нам, чтобы: получить доступ к данным; исправить данные; удалить данные (если это возможно); попросить перестать с вами связываться.
Вы можете отозвать своё согласие в любой момент, связавшись с нами по контактам выше.
Для пользователей ЕС/ЕЭЗ и Великобритании могут применяться дополнительные права по GDPR/UK GDPR. Если вы находитесь в ЕС/ЕЭЗ или Великобритании, вы также имеете право подать жалобу в местный орган по защите персональных данных.`
    }, {
      title: '12) Безопасность',
      content: `Доступ к данным имеет владелец и, при необходимости, только назначенный менеджер/исполнитель проекта. Доступ ограничивается объёмом, необходимым для выполнения работ.`
    }, {
      title: '13) Дети',
      content: `Наши услуги не предназначены для детей. Мы намеренно не собираем данные детей.`
    }, {
      title: '14) Запись звонков',
      content: `Иногда звонки могут записываться для фиксации договорённостей. Вы можете попросить не записывать или удалить запись, если это возможно и законно.`
    }, {
      title: '15) Изменения',
      content: `Мы можем обновлять Политику. Действует актуальная версия, опубликованная на сайте.`
    }]
  },
  ua: {
    title: 'Політика',
    titleHighlight: 'конфіденційності',
    lastUpdated: 'Останнє оновлення: 3 лютого 2026',
    sections: [{
      title: '1) Хто ми',
      content: `IT Monsters — IT-студія, яка створює сучасні сайти для клієнтів по всьому світу.

**Володілець даних (контролер):** ФОП Товкач Максим Павлович (Україна), ІПН 3606206351.

**Контакти з питань приватності:**
- Email: maximtovkach98@gmail.com
- Телефон: +380 50 839 93 09

Також можна звернутися через Telegram/WhatsApp через контакти на нашому сайті.`
    }, {
      title: '2) На кого поширюється',
      content: `Політика застосовується до:
- відвідувачів сайту;
- осіб, які залишають заявку;
- осіб, які пишуть нам у месенджери/на пошту або телефонують;
- клієнтів, які передають матеріали для створення сайту.`
    }, {
      title: '3) Які дані ми збираємо',
      content: `**3.1 Дані з форми на сайті**
Ми можемо отримувати: Ім'я, Email, Номер телефону. За бажанням: Повідомлення/коментар.
Заявка з сайту може надходити нам (наприклад, у канал комунікації в месенджері) для подальшого зв'язку.

**3.2 Дані для створення сайту**
Для виконання послуги клієнт може передати: назву проєкту/компанії; тексти (послуги, описи); зображення, логотип, бренд-матеріали; інші файли/дані, які клієнт надсилає добровільно.
Ми використовуємо ці матеріали лише для виконання замовлення.

**3.3 Переписка**
Якщо ви пишете нам у Telegram/WhatsApp або на email, переписка зберігається у відповідному сервісі. Ми не видаляємо повідомлення, якщо немає запиту на видалення та якщо закон не зобов'язує нас зберігати певні дані.

**3.4 Технічні дані та cookies**
Під час відвідування сайту можуть оброблятися технічні дані (наприклад, IP-адреса, дані браузера/пристрою, cookies, журнали) для роботи сайту та надійної доставки контенту.
Сайт може працювати на сторонніх сервісах (у т.ч. інструментах створення сайтів з використанням ШІ). Такі сервіси можуть обробляти технічні дані відповідно до власних політик.`
    }, {
      title: '4) Для чого ми використовуємо дані',
      content: `Дані потрібні, щоб:
- зв'язатися з вами та відповісти на заявку;
- обговорити ваш майбутній сайт і запропонувати варіанти;
- підготувати безкоштовне демо/попередній варіант (якщо домовлено);
- виконати замовлення та вести правки;
- зберігати документи, якщо цього вимагає законодавство України (в т.ч. бухгалтерія).`
    }, {
      title: '5) Підстави обробки',
      content: `Ми використовуємо:
- **Згоду** (галочка в формі, вибір cookies);
- **Виконання домовленості/договору** (коли ви замовляєте послугу);
- **Юридичний обов'язок** (облік/податки);
- **Законний інтерес** (у межах, необхідних для роботи та захисту сервісу).`
    }, {
      title: '6) Cookies та реклама (Google Ads)',
      content: `На сайті є банер cookies із вибором прийняти або відхилити.
Ми можемо використовувати Google Ads для реклами. Це може включати рекламні та/або конверсійні cookies залежно від налаштувань і вимог закону. Якщо ви відхиляєте відповідні cookies — ми обмежуємо такі технології в межах доступних налаштувань.`
    }, {
      title: '7) Передача даних третім сторонам',
      content: `Ми не продаємо персональні дані.
Дані можуть передаватися лише за потреби для роботи:
- Telegram/WhatsApp (для комунікації та отримання заявок);
- інфраструктурні сервіси (наприклад, Cloudflare) для доставки трафіку та маршрутизації відправки заявок;
- сервіси створення сайтів (у т.ч. з використанням ШІ, наприклад Magic Patterns) для виконання клієнтських проєктів;
- менеджер/виконавець, який призначений на ваш проєкт (лише необхідний доступ);
- у випадках, коли цього вимагає закон.`
    }, {
      title: '8) Міжнародна обробка',
      content: `Оскільки ми працюємо по всьому світу, дані можуть оброблятися в різних країнах залежно від сервісів (месенджери, реклама, інфраструктура). За потреби застосовуються відповідні механізми захисту.`
    }, {
      title: '9) Оплата',
      content: `Ми не приймаємо оплату безпосередньо на сайті. Реквізити для оплати (банківський переказ / крипто тощо) можуть надаватися під час особистого спілкування. Дані банківських карток на сайті ми не обробляємо та не зберігаємо.`
    }, {
      title: '10) Строки зберігання',
      content: `Переписка та матеріали клієнта зберігаються стільки, скільки потрібно для проєкту та ведення записів. Окремі документи (наприклад, бухгалтерські) можуть зберігатися відповідно до законодавства України.
Видалення — вручну за запитом, якщо немає юридичного обов'язку зберігати.`
    }, {
      title: '11) Ваші права',
      content: `Ви можете звернутися до нас, щоб: отримати доступ до даних; виправити дані; видалити дані (якщо це можливо); попросити припинити зв'язок з вами.
Ви можете відкликати свою згоду в будь-який момент, зв'язавшись з нами за контактами вище.
Для користувачів ЄС/ЄЕЗ і Великої Британії можуть діяти додаткові права за GDPR/UK GDPR. Якщо ви перебуваєте в ЄС/ЄЕЗ або Великій Британії, ви також маєте право подати скаргу до місцевого органу з питань захисту персональних даних.`
    }, {
      title: '12) Безпека',
      content: `Доступ до даних має власник і, за потреби, лише призначений менеджер/виконавець проєкту. Доступ обмежується обсягом, необхідним для виконання робіт.`
    }, {
      title: '13) Діти',
      content: `Послуги не призначені для дітей. Ми свідомо не збираємо дані дітей.`
    }, {
      title: '14) Запис дзвінків',
      content: `Іноді дзвінки можуть записуватися для фіксації домовленостей. Ви можете попросити не записувати або видалити запис, якщо це можливо та законно.`
    }, {
      title: '15) Зміни',
      content: `Ми можемо оновлювати Політику. Діє актуальна версія, опублікована на сайті.`
    }]
  },
  cz: {
    title: 'Zásady',
    titleHighlight: 'ochrany osobních údajů',
    lastUpdated: 'Poslední aktualizace: 3. února 2026',
    sections: [{
      title: '1) Kdo jsme',
      content: `IT Monsters je IT agentura, která vytváří moderní webové stránky pro klienty po celém světě.

**Správce údajů:** Fyzická osoba — podnikatel (FOP) Maksym Pavlovyč Tovkač, registrovaný na Ukrajině, DIČ 3606206351.

**Kontakt pro ochranu osobních údajů:**
- Email: maximtovkach98@gmail.com
- Telefon: +380 50 839 93 09

Můžete nás také kontaktovat přes Telegram/WhatsApp pomocí kontaktů na našem webu.`
    }, {
      title: '2) Rozsah',
      content: `Tyto zásady se vztahují na:
- návštěvníky našeho webu;
- osoby, které odesílají poptávku přes formulář;
- osoby, které nás kontaktují přes messenger, telefon nebo email;
- klienty, kteří poskytují materiály potřebné k tvorbě webu.`
    }, {
      title: '3) Jaké údaje shromažďujeme',
      content: `**3.1 Údaje z formuláře na webu**
Můžeme shromažďovat: Jméno, Email, Telefonní číslo. Volitelně: Zpráva/komentář.
Odeslané formuláře nám mohou být doručeny (například do komunikačního kanálu v messengeru), abychom vás mohli kontaktovat.

**3.2 Údaje pro tvorbu webu**
Pokud se stanete klientem, můžete poskytnout materiály potřebné k tvorbě webu: název projektu/firmy; texty (služby, popisy); obrázky, logo, brandové materiály; jakékoli další soubory nebo informace, které nám dobrovolně zašlete.
Tyto materiály používáme výhradně k poskytnutí požadované služby.

**3.3 Komunikace**
Pokud nás kontaktujete přes Telegram/WhatsApp nebo email, konverzace je uložena v příslušné službě. Zprávy nemažeme, pokud o to nepožádáte a pokud nám zákon neukládá povinnost uchovávat určité záznamy.

**3.4 Technické údaje a cookies**
Při návštěvě webu mohou být zpracovávány technické údaje (např. IP adresa, údaje o zařízení/prohlížeči, cookies, logy) pro provoz webu a spolehlivé doručení obsahu.
Náš web může být vytvořen a/nebo provozován pomocí služeb třetích stran (včetně nástrojů pro tvorbu webů s využitím AI). Tyto služby mohou zpracovávat technické údaje podle vlastních zásad ochrany osobních údajů.`
    }, {
      title: '4) Proč vaše údaje používáme',
      content: `Osobní údaje zpracováváme za účelem:
- kontaktovat vás a odpovědět na vaši poptávku;
- porozumět vašemu požadavku a projednat váš budoucí web;
- poskytnout bezplatný náhled/demo, pokud se dohodneme;
- dodat službu (tvorba webu) a provést úpravy;
- vést záznamy vyžadované příslušnými zákony (včetně účetních/daňových záznamů dle ukrajinského práva).`
    }, {
      title: '5) Právní základy',
      content: `V závislosti na vaší poloze se opíráme o:
- **Souhlas** (např. zaškrtávací políčko ve formuláři, volba cookies);
- **Předsmluvní jednání / Plnění smlouvy** (k poskytnutí vámi požadované služby);
- **Právní povinnost** (např. povinné účetní/daňové záznamy);
- **Oprávněný zájem** (omezený na to, co je nezbytné pro provoz a ochranu služby).`
    }, {
      title: '6) Cookies a reklama (Google Ads)',
      content: `Na webu používáme banner cookies, který vám umožňuje přijmout nebo odmítnout určité cookies.
Můžeme používat Google Ads k propagaci našich služeb. V závislosti na vašem regionu a nastavení to může zahrnovat reklamní nebo konverzní cookies. Pokud příslušné cookies odmítnete, omezíme tyto technologie v maximální možné míře.`
    }, {
      title: '7) Sdílení údajů s třetími stranami',
      content: `Osobní údaje neprodáváme.
Údaje sdílíme pouze v nezbytném rozsahu pro poskytování služeb:
- komunikační služby (Telegram/WhatsApp) pro komunikaci s vámi a příjem poptávek;
- infrastrukturní služby (např. Cloudflare) pro doručení provozu a směrování odeslaných formulářů;
- služby tvorby webů (včetně AI nástrojů, jako je Magic Patterns) pro realizaci klientských projektů;
- dodavatelé/zaměstnanci přiřazení k vašemu projektu (pouze ti, kteří na něm skutečně pracují, a pouze s nezbytným přístupem);
- zákonné povinnosti, pokud to vyžaduje zákon.`
    }, {
      title: '8) Mezinárodní přenosy',
      content: `Protože obsluhujeme klienty po celém světě, údaje mohou být zpracovávány v různých zemích v závislosti na používaných službách (messengery, reklama, infrastruktura). Tam, kde je to vyžadováno, uplatňujeme příslušná ochranná opatření.`
    }, {
      title: '9) Platby',
      content: `Na webu nepřijímáme platby přímo. Platební údaje (bankovní převod / krypto apod.) mohou být poskytnuty při osobní komunikaci. Údaje o platebních kartách na webu nezpracováváme ani neukládáme.`
    }, {
      title: '10) Uchovávání údajů',
      content: `Komunikaci a materiály klientů uchováváme po dobu nezbytnou pro projekt a vedení záznamů. Některé záznamy (např. účetní dokumenty) mohou být uchovávány dle požadavků ukrajinského práva.
Údaje mažeme ručně na žádost, pokud nemáme zákonnou povinnost je uchovávat.`
    }, {
      title: '11) Vaše práva',
      content: `Můžete nás kontaktovat s žádostí o přístup k osobním údajům, jejich opravu nebo výmaz, nebo nás požádat, abychom vás přestali kontaktovat.
Svůj souhlas můžete kdykoli odvolat kontaktováním nás na výše uvedených kontaktech.
Pro uživatele v EU/EHP a Spojeném království mohou platit další práva dle GDPR/UK GDPR (přístup, oprava, výmaz, omezení, přenositelnost, námitka, odvolání souhlasu). Pokud se nacházíte v EU/EHP nebo Spojeném království, máte také právo podat stížnost u místního úřadu pro ochranu osobních údajů.`
    }, {
      title: '12) Bezpečnost',
      content: `Používáme přiměřená organizační opatření:
- údaje kontroluje vlastník a sdílí je pouze s manažerem/dodavatelem přiřazeným k vašemu projektu;
- přístup omezujeme na nezbytný rozsah pro dodání služby.`
    }, {
      title: '13) Děti',
      content: `Naše služby nejsou určeny dětem a vědomě neshromažďujeme údaje dětí.`
    }, {
      title: '14) Nahrávání hovorů',
      content: `Příležitostně můžeme nahrávat hovory za účelem dokumentace dohod. Můžete nás požádat, abychom nahrávání zastavili nebo nahrávku smazali, pokud je to proveditelné a zákonné.`
    }, {
      title: '15) Změny',
      content: `Tyto zásady můžeme čas od času aktualizovat. Platná je verze zveřejněná na našem webu.`
    }]
  }
};
export function PrivacyPage() {
  const {
    language
  } = useLanguage();
  const content = privacyContent[language as keyof typeof privacyContent] || privacyContent.en;
  // Simple markdown-like formatting
  const formatContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      // Bold text
      const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      if (line.startsWith('- ')) {
        return <li key={i} className="ml-4" dangerouslySetInnerHTML={{
          __html: formattedLine.substring(2)
        }} data-id="element-365" />;
      }
      if (line.trim() === '') {
        return <br key={i} data-id="element-366" />;
      }
      return <p key={i} className="mb-2" dangerouslySetInnerHTML={{
        __html: formattedLine
      }} data-id="element-367" />;
    });
  };
  return <div className="min-h-screen" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-368">
      <Navigation data-id="element-369" />

      <div className="pt-32 pb-20 px-4" data-id="element-370">
        <div className="max-w-4xl mx-auto" data-id="element-371">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-372">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{
            color: 'var(--text-primary)'
          }} data-id="element-373">
              {content.title}{' '}
              <span style={{
              color: 'var(--accent)'
            }} data-id="element-374">
                {content.titleHighlight}
              </span>
            </h1>
            <p className="text-sm mb-8" style={{
            color: 'var(--text-muted)'
          }} data-id="element-375">
              {content.lastUpdated}
            </p>

            <div className="space-y-6" data-id="element-376">
              {content.sections.map((section, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="rounded-2xl border p-6 sm:p-8" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-377">
                  <h2 className="text-lg sm:text-xl font-bold mb-4" style={{
                color: 'var(--text-primary)'
              }} data-id="element-378">
                    {section.title}
                  </h2>
                  <div className="text-sm sm:text-base leading-relaxed [&>ul]:list-disc [&>ul]:ml-4 [&>ul]:space-y-1 [&_strong]:font-semibold" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-379">
                    {formatContent(section.content)}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>

      <Footer data-id="element-380" />
      <CookieBanner data-id="element-381" />
    </div>;
}